import React, { useMemo, useState } from "react";
import { SectionLabel } from "../components/SectionHeading";
import { MapPin, Calendar } from "lucide-react";
import { useSite } from "../context/SiteContext";
import SmartImage from "../components/SmartImage";

export default function ProjectsPage() {
  const { projects, loading } = useSite();
  const [filter, setFilter] = useState("All");

  const sectors = useMemo(() => {
    const uniq = Array.from(new Set(projects.map((p) => p.sector))).sort();
    return ["All", ...uniq];
  }, [projects]);

  const filtered = useMemo(
    () => (filter === "All" ? projects : projects.filter((p) => p.sector === filter)),
    [filter, projects]
  );

  return (
    <div data-testid="projects-page">
      <section className="border-b border-[#1F3550] relative">
        <div className="absolute inset-0 blueprint-grid opacity-30" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32">
          <SectionLabel code="P/00">Projects</SectionLabel>
          <h1 className="mt-6 font-heading font-black text-4xl md:text-6xl uppercase tracking-tight leading-[0.95] max-w-4xl">
            Real projects.<br /><span className="text-[#F39C12]">Real outcomes</span>.
          </h1>
          <p className="mt-6 text-lg text-[#94A3B8] max-w-3xl leading-relaxed">
            A working sample of the shutdowns, builds, erections and commissioning jobs delivered by TST & MK Group across heavy industries.
          </p>
        </div>
      </section>

      <section className="border-b border-[#1F3550]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 flex flex-wrap items-center gap-3">
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#94A3B8] mr-2">Filter:</span>
          {sectors.map((s) => (
            <button
              key={s}
              data-testid={`filter-${s.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setFilter(s)}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border transition-colors ${
                filter === s
                  ? "border-[#F39C12] text-[#050A0F] bg-[#F39C12]"
                  : "border-[#1F3550] text-[#94A3B8] hover:border-[#F39C12] hover:text-[#F8FAFC]"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </section>

      <section className="border-b border-[#1F3550]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 md:py-24">
          {loading ? (
            <div className="text-center py-20 text-[#94A3B8]">Loading projects...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p) => (
                <article key={p.id} data-testid={`project-${p.id}`} className="group border border-[#1F3550] bg-[#0D1B2A] hover:border-[#F39C12] transition-colors">
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <SmartImage src={p.image} alt={p.title} imgClassName="grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050A0F] via-[#050A0F]/20 to-transparent" />
                    {p.code && <div className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.25em] text-[#F39C12] bg-[#050A0F]/85 px-2 py-1">{p.code}</div>}
                    <div className="absolute top-4 right-4 font-mono text-[10px] tracking-[0.25em] text-[#F8FAFC] bg-[#F39C12]/90 px-2 py-1">{p.sector}</div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading font-bold text-lg text-[#F8FAFC] leading-tight">{p.title}</h3>
                    <p className="mt-3 text-sm text-[#94A3B8] leading-relaxed">{p.description}</p>
                    <div className="mt-5 flex items-center gap-5 font-mono text-[10px] tracking-[0.25em] uppercase text-[#64748B]">
                      <span className="flex items-center gap-2"><MapPin className="h-3 w-3 text-[#F39C12]" />{p.location}</span>
                      <span className="flex items-center gap-2"><Calendar className="h-3 w-3 text-[#F39C12]" />{p.year}</span>
                    </div>
                  </div>
                </article>
              ))}
              {filtered.length === 0 && <div className="col-span-full text-center py-20 text-[#94A3B8]">No projects in this category yet.</div>}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
